import type { AuthUser } from '@/types/auth.types'
import type { TenantType, PlanCode } from '@/types/tenant.types'
import type { CanonicalUserRole } from './accessContract'
import { resolveRole } from './accessContract'

export interface UiAccessPolicy {
  allowedRoles?: readonly CanonicalUserRole[]
  requiredPermissions?: readonly string[]
  anyPermissions?: readonly string[]
  requiredFeatures?: readonly string[]
  disallowedTenantTypes?: readonly TenantType[]
  disallowedPlans?: readonly PlanCode[]
}

export const ROUTE_ACCESS = {
  vaultShell: {
    allowedRoles: [
      'independent_client',
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
      'individual_client',
      'business_owner_client',
      'authorized_client',
    ],
  },
  clientVault: {
    allowedRoles: [
      'individual_client',
      'business_owner_client',
      'authorized_client',
      'independent_client',
    ],
  },
  firmVault: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
    ],
    requiredPermissions: ['txh.vault.read'],
  },
  firmSwitches: {
    allowedRoles: ['firm_owner', 'firm_admin'],
    requiredPermissions: ['txh.vault.relationship.manage'],
  },
  claimVault: { allowedRoles: ['individual_client', 'business_owner_client'] },
  tenantShell: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
      'individual_client',
      'business_owner_client',
      'authorized_client',
    ],
  },
  platformShell: { allowedRoles: ['cml_admin', 'cml_monitor', 'cml_operations'] },
  platformOverview: {
    allowedRoles: ['cml_admin', 'cml_monitor'],
    anyPermissions: ['txh.platform.tenant.read', 'txh.monitoring.service.read'],
  },
  platformTenants: {
    allowedRoles: ['cml_admin', 'cml_monitor'],
    requiredPermissions: ['txh.platform.tenant.read'],
  },
  platformUsers: {
    allowedRoles: ['cml_admin', 'cml_monitor'],
    requiredPermissions: ['txh.platform.user.read'],
  },
  platformMonitoring: {
    allowedRoles: ['cml_admin', 'cml_monitor', 'cml_operations'],
    requiredPermissions: ['txh.monitoring.service.read'],
  },
  platformBusinessSubjectReview: {
    allowedRoles: ['cml_admin'],
    requiredPermissions: [
      'txh.platform.legal_control.subject.review',
      'txh.platform.legal_control.subject.inspect',
    ],
  },
  platformSubjectReview: {
    allowedRoles: ['cml_admin'],
    requiredPermissions: ['txh.platform.legal_control.subject.review'],
  },
  platformPolicyGovernance: {
    allowedRoles: ['cml_admin', 'cml_operations'],
    anyPermissions: [
      'txh.platform.legal_control.policy.submit',
      'txh.platform.legal_control.policy.decide',
      'txh.platform.legal_control.policy.retire',
    ],
  },
  platformOperations: {
    allowedRoles: ['cml_admin', 'cml_operations'],
    requiredPermissions: ['txh.monitoring.operation.read'],
  },
  platformAudit: {
    allowedRoles: ['cml_admin', 'cml_monitor'],
    requiredPermissions: ['txh.platform.audit.read'],
  },
  platformDeletions: {
    allowedRoles: ['cml_admin', 'cml_monitor'],
    requiredPermissions: ['txh.platform.deletion.read'],
  },
  platformSupport: {
    allowedRoles: ['cml_admin', 'cml_monitor'],
    anyPermissions: ['txh.support.session.read', 'txh.support.session.create'],
  },
  platformProfile: { allowedRoles: ['cml_admin', 'cml_monitor', 'cml_operations'] },
  platformSettings: { allowedRoles: ['cml_admin', 'cml_monitor', 'cml_operations'] },
  home: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
      'individual_client',
      'business_owner_client',
      'authorized_client',
    ],
  },
  dashboard: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
    ],
  },
  profile: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
      'individual_client',
      'business_owner_client',
      'authorized_client',
    ],
  },
  settings: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
      'individual_client',
      'business_owner_client',
      'authorized_client',
    ],
  },
  users: { allowedRoles: ['firm_owner', 'firm_admin'], requiredPermissions: ['txh.user.read'] },
  userDetail: {
    allowedRoles: ['firm_owner', 'firm_admin'],
    requiredPermissions: ['txh.user.read'],
  },
  sessions: {
    allowedRoles: ['firm_owner', 'firm_admin'],
    requiredPermissions: ['txh.session.read'],
  },
  audit: {
    allowedRoles: ['firm_owner', 'firm_admin', 'ca_partner'],
    requiredPermissions: ['txh.audit.view'],
  },
  recycleBin: {
    allowedRoles: ['firm_owner', 'firm_admin'],
    requiredPermissions: ['txh.deletion.read'],
  },
  securitySettings: {
    allowedRoles: ['firm_owner', 'firm_admin'],
    requiredPermissions: ['txh.settings.read'],
  },
  organizationSettings: {
    allowedRoles: ['firm_owner', 'firm_admin'],
    requiredPermissions: ['txh.settings.read'],
  },
  rulePacks: {
    allowedRoles: ['firm_owner', 'ca_partner'],
    requiredPermissions: ['txh.assessment.rule_pack.read'],
  },
  billing: { allowedRoles: ['firm_owner'], requiredPermissions: ['txh.settings.billing'] },
  search: {
    anyPermissions: [
      'txh.client.read',
      'txh.document.read',
      'txh.assessment.read',
      'txh.gst_assessment.read',
      'txh.payment.read',
      'txh.insight.read',
    ],
  },
  customers: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
    ],
    requiredPermissions: ['txh.client.read'],
  },
  customerDetail: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
    ],
    requiredPermissions: ['txh.client.read'],
  },
  assessmentDetail: {
    allowedRoles: ['firm_owner', 'firm_admin', 'ca_partner'],
    requiredPermissions: ['txh.assessment.read'],
  },
  returnCases: {
    allowedRoles: ['firm_owner', 'firm_admin', 'ca_partner'],
    requiredPermissions: ['txh.assessment.case.read'],
  },
  clientPortal: {
    allowedRoles: ['individual_client'],
    requiredPermissions: ['txh.client.read'],
  },
  businessPortal: {
    allowedRoles: ['business_owner_client', 'authorized_client'],
    requiredPermissions: ['txh.client.access.read'],
  },
  assistantWorkbench: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
    ],
    anyPermissions: ['txh.assessment.work.read', 'txh.assessment.research.read'],
  },
  practiceGovernance: {
    allowedRoles: ['firm_owner', 'firm_admin', 'ca_partner'],
    disallowedTenantTypes: ['solo_ca'],
    disallowedPlans: ['starter'],
    anyPermissions: [
      'txh.governance.workload.read',
      'txh.governance.review_policy.read',
      'txh.governance.policy.read',
      'txh.branch.read',
    ],
  },
  payments: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'individual_client',
      'business_owner_client',
      'authorized_client',
    ],
    requiredPermissions: ['txh.payment.read'],
    requiredFeatures: ['payments.view'],
  },
  documents: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
      'individual_client',
      'business_owner_client',
      'authorized_client',
    ],
    requiredPermissions: ['txh.document.read'],
    requiredFeatures: ['documents.view'],
  },
  documentDetail: {
    allowedRoles: [
      'firm_owner',
      'firm_admin',
      'ca_partner',
      'technical_assistant',
      'research_assistant',
      'individual_client',
      'business_owner_client',
      'authorized_client',
    ],
    requiredPermissions: ['txh.document.read'],
    requiredFeatures: ['documents.view'],
  },
} as const satisfies Record<string, UiAccessPolicy>

export type RouteAccessId = keyof typeof ROUTE_ACCESS
export type AccessDenialReason =
  | 'allowed'
  | 'missing_identity'
  | 'unsupported_persona'
  | 'missing_permission'
  | 'missing_feature'
  | 'unsupported_tenant_type'
  | 'unsupported_plan'

export interface UiAccessDecision {
  allowed: boolean
  reason: AccessDenialReason
}

function matchesGrant(grants: readonly string[], required: string): boolean {
  return grants.some(grant => {
    const normalized = grant.trim().toLowerCase()
    return (
      normalized === required ||
      normalized === '*' ||
      normalized === 'txh.*' ||
      (normalized.endsWith('.*') && required.startsWith(normalized.slice(0, -1)))
    )
  })
}

export function evaluateUiAccess(
  user:
    | (Pick<AuthUser, 'role' | 'permissions' | 'features'> &
        Partial<Pick<AuthUser, 'tenantType' | 'planCode' | 'authScope'>>)
    | null
    | undefined,
  policy: UiAccessPolicy
): UiAccessDecision {
  if (!user) return { allowed: false, reason: 'missing_identity' }
  const canonicalRole = resolveRole(user.role).canonicalRole
  if (!canonicalRole) return { allowed: false, reason: 'unsupported_persona' }
  if (policy.allowedRoles && !policy.allowedRoles.includes(canonicalRole)) {
    return { allowed: false, reason: 'unsupported_persona' }
  }
  if (
    policy.disallowedTenantTypes &&
    user.tenantType &&
    policy.disallowedTenantTypes.includes(user.tenantType)
  ) {
    return { allowed: false, reason: 'unsupported_tenant_type' }
  }
  if (policy.disallowedPlans && user.planCode && policy.disallowedPlans.includes(user.planCode)) {
    return { allowed: false, reason: 'unsupported_plan' }
  }
  const permissions = user.permissions ?? []
  if (policy.requiredPermissions?.some(permission => !matchesGrant(permissions, permission))) {
    return { allowed: false, reason: 'missing_permission' }
  }
  if (
    policy.anyPermissions &&
    !policy.anyPermissions.some(permission => matchesGrant(permissions, permission))
  ) {
    return { allowed: false, reason: 'missing_permission' }
  }
  const features = (user.features ?? []).map(feature => feature.toLowerCase())
  const hasFeature = (required: string) =>
    features.some(
      available =>
        available === required ||
        available.startsWith(`${required}.`) ||
        required.startsWith(`${available}.`)
    )
  if (policy.requiredFeatures?.some(feature => !hasFeature(feature.toLowerCase()))) {
    return { allowed: false, reason: 'missing_feature' }
  }
  return { allowed: true, reason: 'allowed' }
}

export function evaluateRouteAccess(
  user:
    | (Pick<AuthUser, 'role' | 'permissions' | 'features'> &
        Partial<Pick<AuthUser, 'tenantType' | 'planCode' | 'authScope'>>)
    | null
    | undefined,
  route: RouteAccessId
): UiAccessDecision {
  if (
    user &&
    resolveRole(user.role).canonicalRole === 'independent_client' &&
    user.authScope !== 'client_vault'
  ) {
    return { allowed: false, reason: 'unsupported_persona' }
  }
  if (user?.authScope === 'client_vault' && !['vaultShell', 'clientVault'].includes(route)) {
    return { allowed: false, reason: 'unsupported_persona' }
  }
  if (route === 'clientVault' && user?.authScope !== 'client_vault') {
    return { allowed: false, reason: 'unsupported_persona' }
  }
  return evaluateUiAccess(user, ROUTE_ACCESS[route])
}

export function canAccessRoute(
  user:
    | (Pick<AuthUser, 'role' | 'permissions' | 'features'> &
        Partial<Pick<AuthUser, 'tenantType' | 'planCode' | 'authScope'>>)
    | null
    | undefined,
  route: RouteAccessId
): boolean {
  return evaluateRouteAccess(user, route).allowed
}
